import { Alert, Input, Layouts, Loader, UploadImages, TextArea } from 'components'
import s from './styles.module.scss'
import DashBoardNav from '../../dashboardNav'
import { useState, useEffect, useCallback, useRef } from 'react'
import * as api from 'api'
import { head, imageCompressor } from 'helpers'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST

export default function Main() {
  const [loading, setLoading] = useState(false)
  const [placements, setPlacements] = useState([])
  const [newPlacements, setNewPlacements] = useState([])
  const [placementsImages, setPlacementsImages] = useState([])
  const [placementsImagesUrls, setPlacementsImagesUrls] = useState([])
  const [newPlacementsImages, setNewPlacementsImages] = useState([])
  const [newPlacementsImagesUrls, setNewPlacementsImagesUrls] = useState([])
  const [placementsToDelete, setPlacementsToDelete] = useState([])
  const processing = useRef(false)

  const getPlacements = useCallback(async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    try {
      const res = await api.admin.featuredPlacement.fetch({})

      if (res?.code === 200) {
        const fetched = res.payload?.placements || []
        setPlacements(fetched)
        setPlacementsImages(Array(fetched.length).fill(null))
        setPlacementsImagesUrls(fetched.map(p => (p.image ? IMAGE_HOST + p.image : null)))
      } else {
        Alert.error(res?.message || 'Failed to fetch featured placements')
      }
    } catch (error) {
      console.error('Error fetching featured placements:', error)
      Alert.error('Failed to load featured placements')
    }

    setLoading(false)
    processing.current = false
  }, [])

  useEffect(() => {
    getPlacements()
  }, [getPlacements])

  useEffect(() => {
    head({ title: 'Featured Placements | BCN' })
  }, [])

  const handleChange = (index, field, value) => {
    const updated = [...placements]
    updated[index] = { ...updated[index], [field]: value }
    setPlacements(updated)
  }

  const handleImageChange = async (e, index) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const compressedFiles = await imageCompressor([file], 'listing')
      if (compressedFiles[0]) {
        const imageUrl = URL.createObjectURL(compressedFiles[0])
        const updatedImages = [...placementsImages]
        const updatedUrls = [...placementsImagesUrls]
        updatedImages[index] = compressedFiles[0]
        updatedUrls[index] = imageUrl
        setPlacementsImages(updatedImages)
        setPlacementsImagesUrls(updatedUrls)
      }
    } catch (error) {
      console.error('Error compressing image:', error)
      Alert.error('Failed to process image')
    }
  }

  const handleRemove = (index) => {
    const placement = placements[index]
    if (placement._id) {
      setPlacementsToDelete([...placementsToDelete, placement._id])
    }
    setPlacements(placements.filter((_, i) => i !== index))
    setPlacementsImages(placementsImages.filter((_, i) => i !== index))
    setPlacementsImagesUrls(placementsImagesUrls.filter((_, i) => i !== index))
  }

  const handleNewChange = (index, field, value) => {
    const updated = [...newPlacements]
    updated[index] = { ...updated[index], [field]: value }
    setNewPlacements(updated)
  }

  const handleNewImageChange = async (e, index) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const compressedFiles = await imageCompressor([file], 'listing')
      if (compressedFiles[0]) {
        const imageUrl = URL.createObjectURL(compressedFiles[0])
        const updatedImages = [...newPlacementsImages]
        const updatedUrls = [...newPlacementsImagesUrls]
        updatedImages[index] = compressedFiles[0]
        updatedUrls[index] = imageUrl
        setNewPlacementsImages(updatedImages)
        setNewPlacementsImagesUrls(updatedUrls)
      }
    } catch (error) {
      console.error('Error compressing image:', error)
      Alert.error('Failed to process image')
    }
  }

  const handleRemoveNew = (index) => {
    setNewPlacements(newPlacements.filter((_, i) => i !== index))
    setNewPlacementsImages(newPlacementsImages.filter((_, i) => i !== index))
    setNewPlacementsImagesUrls(newPlacementsImagesUrls.filter((_, i) => i !== index))
  }

  const addNew = () => {
    setNewPlacements([...newPlacements, { title: '', link: '', description: '', order: placements.length + newPlacements.length }])
    setNewPlacementsImages([...newPlacementsImages, null])
    setNewPlacementsImagesUrls([...newPlacementsImagesUrls, null])
  }

  const saveHandler = async () => {
    for (const p of placements) {
      if (!p.title?.trim()) {
        Alert.warn('Please fill title for all existing placements')
        return
      }
    }
    for (const p of newPlacements) {
      if (!p.title?.trim()) {
        Alert.warn('Please fill title for all new placements')
        return
      }
    }

    if (processing.current) return
    processing.current = true
    Alert.success('Saving featured placements...')
    setLoading(true)

    try {
      for (const id of placementsToDelete) {
        await api.admin.featuredPlacement.delete(id)
      }

      for (let i = 0; i < placements.length; i++) {
        const p = placements[i]
        const data = new FormData()
        data.append('title', p.title)
        if (p.link) data.append('link', p.link)
        if (p.description) data.append('description', p.description)
        if (p.order !== undefined) data.append('order', p.order)
        if (placementsImages[i]) data.append('image', placementsImages[i])
        await api.admin.featuredPlacement.update(p._id, data)
      }

      for (let i = 0; i < newPlacements.length; i++) {
        const p = newPlacements[i]
        const data = new FormData()
        data.append('title', p.title)
        if (p.link?.trim()) data.append('link', p.link.trim())
        if (p.description?.trim()) data.append('description', p.description.trim())
        if (p.order !== undefined) data.append('order', p.order)
        if (newPlacementsImages[i]) data.append('image', newPlacementsImages[i])

        const createRes = await api.admin.featuredPlacement.create(data)
        if (createRes?.code !== 201 && createRes?.code !== 200) {
          Alert.error(`Failed to create "${p.title}": ${createRes?.message || 'Unknown error'}`)
        }
      }

      Alert.success('Featured placements saved successfully')
      setNewPlacements([])
      setNewPlacementsImages([])
      setNewPlacementsImagesUrls([])
      setPlacementsToDelete([])

      await new Promise(resolve => setTimeout(resolve, 500))
      await getPlacements()
    } catch (error) {
      console.error('Error saving featured placements:', error)
      Alert.error('Failed to save featured placements')
    }

    setLoading(false)
    processing.current = false
  }

  return (
    <div className={s.main}>
      <div className={s.outer}>
        <Layouts.Classic title='Featured Placements'>
          <div className={s.headerBottom}>
            <div className={s.saveButton} onClick={saveHandler}>
              <span className='material-icons-outlined'>save</span> Save
            </div>
          </div>
        </Layouts.Classic>

        {!loading ? (
          <div className={s.content + ' innerScrollX'}>
            <DashBoardNav />

            <div className={s.list}>
              <div className={s.label}>Featured Placements {placements.length > 0 && `(${placements.length})`}</div>

              {placements.length === 0 && newPlacements.length === 0 && (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                  No featured placements yet. Click "Add Placement" to create one.
                </div>
              )}

              {placements.map((placement, i) => (
                <PlacementForm
                  key={`existing-${placement._id || i}`}
                  index={i}
                  placement={placement}
                  imageUrl={placementsImagesUrls[i]}
                  onChange={handleChange}
                  onImageChange={handleImageChange}
                  onRemove={handleRemove}
                  isNew={false}
                />
              ))}

              {newPlacements.map((placement, i) => (
                <PlacementForm
                  key={`new-${i}`}
                  index={i}
                  displayIndex={placements.length + i}
                  placement={placement}
                  imageUrl={newPlacementsImagesUrls[i]}
                  onChange={handleNewChange}
                  onImageChange={handleNewImageChange}
                  onRemove={handleRemoveNew}
                  isNew={true}
                />
              ))}
            </div>

            <div className={s.addMore}>
              <div onClick={addNew}>
                Add Placement <span className='material-icons-outlined'>add_circle_outline</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={s.loader}>
            <Loader message='Loading featured placements...' color='var(--c-primary)' colorText='var(--c-primary)' />
          </div>
        )}
      </div>
    </div>
  )
}

const PlacementForm = ({ index, displayIndex, placement, imageUrl, onChange, onImageChange, onRemove, isNew }) => {
  const actualIndex = displayIndex !== undefined ? displayIndex : index

  return (
    <div className={s.card}>
      <div className={s.cardTitle}>
        <span>Placement {actualIndex + 1}</span>
        <span onClick={() => onRemove(index)}>
          Remove <span className='material-icons-outlined'>cancel</span>
        </span>
      </div>

      <div className={s.imageUpload}>
        <UploadImages.Single
          userLogoUrl={imageUrl || ''}
          handleUserLogoChange={(e) => onImageChange(e, index)}
          id={`placementImage-${isNew ? 'new' : 'existing'}-${index}`}
        />
      </div>

      <Input.Classic
        type='text'
        label='Title *'
        iconLeft='title'
        placeholder='Enter placement title'
        value={placement.title || ''}
        onChange={(e) => onChange(index, 'title', e.target.value)}
      />

      <Input.Classic
        type='url'
        label='Link (Optional)'
        iconLeft='link'
        placeholder='https://example.com'
        value={placement.link || ''}
        onChange={(e) => onChange(index, 'link', e.target.value)}
      />

      <TextArea.Classic
        label='Description (Optional)'
        iconLeft='description'
        placeholder='Enter a short description'
        value={placement.description || ''}
        rows='3'
        onChange={(e) => onChange(index, 'description', e.target.value)}
      />

      <Input.Classic
        type='number'
        label='Display Order'
        iconLeft='sort'
        placeholder='0'
        value={placement.order !== undefined ? placement.order : ''}
        onChange={(e) => onChange(index, 'order', parseInt(e.target.value) || 0)}
      />
    </div>
  )
}
